import _ from 'underscore';
import * as jsondiffpatch from 'jsondiffpatch';
import { createSlice } from '@reduxjs/toolkit';

const defaultState = {
    games: [],
    newGameInstance: 0,
    users: [],
    messages: [],
    windowBlurred: false,
    searching: false
};

const handleGameState = (state, game, username) => {
    state.currentGame = jsondiffpatch.clone(game);

    const currentState = state.currentGame;
    if (!currentState) {
        state.newGame = false;
        return;
    }

    if (
        currentState.spectators.some((spectator) => {
            return spectator.name === username;
        })
    ) {
        return;
    }

    if (currentState) {
        delete state.passwordGame;
        delete state.passwordJoinType;
        delete state.passwordError;
    }

    if (state.currentGame && !state.currentGame.started) {
        state.newGame = false;
    }
};

const handleMessage = (state, message, args) => {
    switch (message) {
        case 'games': {
            state.games = args[0];
            if (
                state.currentGame &&
                !args[0].some((game) => {
                    return game.id === state.currentGame.id;
                })
            ) {
                state.currentGame = undefined;
                state.newGame = false;
            }
            break;
        }
        case 'newgame':
            state.games = [...args[0], ...state.games];
            break;
        case 'removegame':
            if (
                state.currentGame &&
                !state.currentGame.started &&
                args[0].some((g) => g.id === state.currentGame.id)
            ) {
                state.currentGame = undefined;
                state.newGame = false;
                state.gameError = 'The game has timed out and is no longer available.';
            }
            state.games = state.games.filter((game) => !args[0].some((g) => g.id === game.id));
            break;
        case 'updategame': {
            const updatedGames = state.games.slice(0);
            for (const game of args[0]) {
                const index = _.findIndex(updatedGames, (g) => g.id === game.id);

                // ARCHON: an update for a game this client has not seen listed
                // yet - a tournament table opened for a match while the lobby
                // list was filtered, say - used to be written to index -1 and
                // dropped. Add it instead: an update is the server saying this
                // game exists, and a table nobody can see is a table nobody
                // joins.
                if (index === -1) {
                    updatedGames.push(game);
                } else {
                    updatedGames[index] = game;
                }
            }
            state.games = updatedGames;

            /**
             * Keep the one flag the list is authoritative about in step.
             *
             * `currentGame` otherwise holds whatever `gamestate` last said, and
             * a table that starts is announced by `updategame` alone - so the
             * copy the client is looking at stayed unstarted forever. That
             * matters below: `removegame` reads `started` to decide whether a
             * table that disappeared had timed out, and a tournament table
             * disappearing mid-series is the ordinary end of a game, not a
             * failure. Only `started` - the rest of this summary is the lobby
             * list's view, not the seated player's.
             */
            if (state.currentGame) {
                const mine = args[0].find((game) => game.id === state.currentGame.id);

                if (mine) {
                    state.currentGame.started = mine.started;
                }
            }
            break;
        }
        case 'users':
            state.users = args[0];
            break;
        case 'newuser': {
            const users = state.users.slice(0);
            users.push(args[0]);
            state.users = users.sort((a, b) => a < b);
            break;
        }
        case 'userleft':
            state.users = state.users.filter((u) => u.username !== args[0].username);
            break;
        case 'passworderror':
            state.passwordError = args[0];
            break;
        case 'gameerror':
            state.gameError = args[0];
            break;
        case 'lobbychat':
            state.messages = [...state.messages, args[0]];
            break;
        case 'lobbynotice':
            // Numbered so two identical notices in a row both show.
            state.notice = { ...(args[0] || {}), id: ((state.notice && state.notice.id) || 0) + 1 };
            break;
        case 'nochat':
            state.lobbyError = true;
            break;
        case 'lobbymessages':
            state.messages = args[0];
            break;
        case 'removemessage': {
            const messageItem = state.messages.find(
                (messageItem) => messageItem.id === parseInt(args[0])
            );
            if (messageItem) {
                messageItem.deletedBy = args[1];
                messageItem.deleted = true;
                state.messages = [].concat(state.messages);
            }
            break;
        }
        case 'banner':
            state.bannerNotice = args[0];
            break;
        case 'motd':
            state.motd = args[0];
            break;
        case 'gamestate':
            handleGameState(state, args[0], args[1]);
            break;
        case 'cleargamestate':
            state.newGame = false;
            state.currentGame = undefined;
            break;
        case 'matchmaking': {
            // Quick Match status from the server: searching | matched | error | idle.
            const info = args[0] || {};
            if (info.status === 'searching') {
                state.searching = true;
                state.matchmakingFormat = info.format;
                state.matchmakingQueued = info.queued;
                state.matchmakingError = undefined;
            } else if (info.status === 'matched') {
                // The paired game arrives via 'gamestate' right after this.
                state.searching = false;
                state.matchmakingError = undefined;
            } else if (info.status === 'error') {
                state.searching = false;
                state.matchmakingError = info.message;
            } else {
                // 'idle' / cancelled
                state.searching = false;
                state.matchmakingError = undefined;
            }
            break;
        }
    }
};

const lobbySlice = createSlice({
    name: 'lobby',
    initialState: defaultState,
    reducers: {
        connecting: (state, action) => {
            state.connecting = true;
            state.connected = false;
            state.socket = action.payload.socket;
        },
        connected: (state) => {
            state.connecting = false;
            state.connected = true;
        },
        disconnected: (state) => {
            state.connecting = false;
            state.connected = false;
        },
        reconnecting: (state) => {
            state.connected = false;
            state.connecting = true;
        },
        messageReceived: (state, action) => {
            handleMessage(state, action.payload.message, action.payload.args);
        },
        joinPasswordGame: (state, action) => {
            state.passwordGame = action.payload.game;
            state.passwordJoinType = action.payload.joinType;
        },
        cancelPasswordJoin: (state) => {
            state.passwordJoinType = undefined;
            state.passwordGame = undefined;
            state.passwordError = undefined;
        },
        gameSocketClosed: (state) => {
            state.currentGame = undefined;
            state.newGame = false;
            state.rootState = undefined;
        },
        gameSocketDisconnected: (state) => {
            state.rootState = undefined;
        },
        gameStarting: (state) => {
            state.gameError = undefined;
        },
        startNewGame: (state) => {
            state.newGame = true;
            state.newGameInstance += 1;
            state.gameError = undefined;
            state.passwordError = undefined;
        },
        cancelNewGame: (state) => {
            state.newGame = false;
        },
        startSearching: (state, action) => {
            state.searching = true;
            state.matchmakingFormat = action.payload;
            state.matchmakingError = undefined;
        },
        stopSearching: (state) => {
            state.searching = false;
        },
        clearMatchmakingError: (state) => {
            state.matchmakingError = undefined;
        },
        clearChatStatus: (state) => {
            state.lobbyError = false;
        },
        clearGameState: (state) => {
            state.newGame = false;
            state.currentGame = undefined;
        },
        clearGameError: (state) => {
            state.gameError = undefined;
        },
        clearNotice: (state) => {
            state.notice = undefined;
        },
        windowBlur: (state) => {
            state.windowBlurred = true;
        },
        windowFocus: (state) => {
            state.windowBlurred = false;
        },
        responseTimeReceived: (state, action) => {
            state.responseTime = action.payload;
        },
        setRootState: (state, action) => {
            state.rootState = action.payload;
        }
    }
});

export const lobbyActions = lobbySlice.actions;
export default lobbySlice.reducer;
